import React, { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  TextArea,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { ADMIN_LOAN_DETAIL_KEY } from './constants';
import { ApproveApplicationModalProps } from './types';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const ApproveApplicationModal = ({
  isOpen,
  onDismiss,
  loanId,
  onSuccess,
}: ApproveApplicationModalProps) => {
  const [remarks, setRemarks] = useState('');
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();

  const approveMutation = useMutation({
    mutationFn: () =>
      api.loans.updateLoanStatus(loanId!, {
        status: 'loan-approved',
        remarks: remarks || null,
      }),
    onSuccess: () => {
      setRemarks('');
      queryClient.invalidateQueries(['LOANS']);
      queryClient.invalidateQueries([ADMIN_LOAN_DETAIL_KEY, loanId]);
      onDismiss();
      onSuccess?.();
      showToast({ content: 'Request successfully approved.', color: 'positive', duration: 4000 });
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to approve request. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="small" zIndex={overlayLayers.modal}>
      <ModalHeader title="Approve Advance" />
      <ModalBody>
        <Box display="flex" flexDirection="column" gap="spacing.5">
          <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
            Are you sure you want to approve this advance?
          </Text>
          <TextArea
            label="Remarks"
            necessityIndicator="optional"
            placeholder="Enter any remarks/comments before approving"
            value={remarks}
            onChange={({ value }) => setRemarks(value ?? '')}
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.4" width="100%">
          <Button variant="secondary" isFullWidth onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            isFullWidth
            isLoading={approveMutation.isLoading}
            onClick={() => approveMutation.mutate()}>
            Approve
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
